import { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { Sidebar, NarrativeView, CreateWorldWizard, SettingsModal, ImportWorldWizard, WorldRail, CreditRequiredModal } from '../components';
import { CompanionMemoryModal } from '../components/CompanionMemoryModal';
import { CompanionSettingsModal } from '../components/CompanionSettingsModal';
import { useAuthStore, useWorldsStore, useActiveWorldStore } from '../store';
import { useSidebarStore } from '../store/ui';
import { useWebSocket, useIsMobile } from '../hooks';
import { getWorld, getAvailableModels, updateCompanionProfile, branchWorld, ModelInfo } from '../api';
import { WorldSeed } from '../types';

export function WorldPage() {
  const { worldId } = useParams<{ worldId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  const { user, isAuthenticated, isLoading: authLoading } = useAuthStore();
  const { worlds, fetchWorlds } = useWorldsStore();
  const {
    worldState,
    messages,
    isStreaming,
    streamingContent,
    companionProfile,
    setCompanionProfile,
    reset: resetActiveWorld
  } = useActiveWorldStore();
  const ephemeralNotices = useActiveWorldStore(state => state.ephemeralNotices);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportWizard, setShowImportWizard] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [importHandoff, setImportHandoff] = useState<{ title: string; prompt: string } | null>(null);
  const [isLoadingWorld, setIsLoadingWorld] = useState(true);
  const [showCompanionMemories, setShowCompanionMemories] = useState(false);
  const [showCompanionSettings, setShowCompanionSettings] = useState(false);
  const [showCreditRequired, setShowCreditRequired] = useState(false);
  const [dismissedCreditNoticeId, setDismissedCreditNoticeId] = useState<string | null>(null);
  const [availableModels, setAvailableModels] = useState<ModelInfo[]>([]);
  
  const sidebarOpen = useSidebarStore(state => state.sidebarOpen);
  const toggleSidebar = useSidebarStore(state => state.toggleSidebar);
  const setSidebarOpen = useSidebarStore(state => state.setSidebarOpen);
  const isAdmin = !!user?.is_admin;
  const isMobile = useIsMobile();
  const insufficientCreditsNotice = useMemo(() => {
    for (let i = ephemeralNotices.length - 1; i >= 0; i -= 1) {
      const notice = ephemeralNotices[i];
      if (notice.errorDetails?.code === 'INSUFFICIENT_CREDITS') return notice;
    }
    return null;
  }, [ephemeralNotices]);

  useEffect(() => {
    if (!insufficientCreditsNotice) return;
    if (insufficientCreditsNotice.id === dismissedCreditNoticeId) return;
    setShowCreditRequired(true);
  }, [insufficientCreditsNotice, dismissedCreditNoticeId]);

  // Auto-collapse sidebar when entering an in-world view. The world list
  // is chrome here; the chat + right rail are the protagonists. User can
  // reopen via the header hamburger or the `[` keyboard shortcut.
  useEffect(() => {
    if (worldId && !isMobile) {
      setSidebarOpen(false);
    }
  }, [worldId, isMobile, setSidebarOpen]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Fetch worlds list
  useEffect(() => {
    if (isAuthenticated && worlds.length === 0) {
      fetchWorlds();
    }
  }, [isAuthenticated, worlds.length, fetchWorlds]);

  // Load world details
  useEffect(() => {
    if (!worldId || !isAuthenticated) return;

    setIsLoadingWorld(true);
    resetActiveWorld();

    getWorld(worldId)
      .then(({ world, seed, currentChapter, usageStats }) => {
        const currentState = useActiveWorldStore.getState();
        const activeCharacters = currentState.worldState?.activeCharacters
          ?? currentState.pendingActiveCharacters
          ?? [];
        useActiveWorldStore.getState().setWorldState({
          world,
          seed,
          currentArc: null,
          currentChapter: null,
          activeCharacters,
          recentMessages: [],
        });
        // Set chapter number separately for status bar
        useActiveWorldStore.getState().setCurrentChapterNumber(currentChapter?.chapter_number ?? null);
        // A branch made at an input arrives with that input to send again.
        const prefill = (location.state as { prefill?: string | null } | null)?.prefill;
        if (prefill) {
          useActiveWorldStore.setState({ restoredInput: prefill });
          navigate(location.pathname, { replace: true, state: null });
        }
        // Set total cumulative cost
        if (usageStats) {
          useActiveWorldStore.getState().setTotalCost(
            usageStats.totalTokens,  // For now use total tokens as both prompt and completion
            0,
            usageStats.totalCostMicros
          );
        }
      })
      .catch((error) => {
        console.error('Failed to load world:', error);
        navigate('/', { replace: true });
      })
      .finally(() => {
        setIsLoadingWorld(false);
      });

    return () => {
      resetActiveWorld();
    };
  }, [worldId, isAuthenticated, navigate, resetActiveWorld]);

  // WebSocket connection
  const { sendUserInput, editMessage, deleteMessage, regenerateResponse, stopGeneration, loadOlderMessages, isConnected } = useWebSocket({
    worldId: worldId || '',
    userId: user?.id || '',
    onError: (error) => {
      console.error('WebSocket error:', error);
    },
  });

  const handleBranchMessage = useCallback(async (messageId: string) => {
    if (!worldId) return;
    const branch = await branchWorld(worldId, messageId);
    await fetchWorlds();
    navigate(`/world/${branch.worldId}`, { state: { prefill: branch.prefill } });
  }, [worldId, fetchWorlds, navigate]);

  // Handle settings update - update local state immediately
  const handleSettingsUpdated = useCallback((updatedSeed: Partial<WorldSeed>, updatedTitle?: string) => {
    const currentState = useActiveWorldStore.getState().worldState;
    if (!currentState || !currentState.seed) return;
    
    // Update world state with new settings
    useActiveWorldStore.getState().setWorldState({
      ...currentState,
      world: {
        ...currentState.world,
        title: updatedTitle ?? currentState.world.title,
      },
      seed: {
        ...currentState.seed,
        ...updatedSeed,
      } as WorldSeed,
    });
  }, []);

  // Fetch available models for admin companion model picker
  useEffect(() => {
    if (isAdmin && worldState?.world.simulation_type === 'companion') {
      getAvailableModels().then(setAvailableModels).catch(() => {});
    }
  }, [isAdmin, worldState?.world.simulation_type]);

  const handleCompanionModelChange = useCallback(async (value: string) => {
    if (!worldId) return;
    const [provider, model] = value === 'default'
      ? [null, null]
      : [value.split('/')[0], value.split('/').slice(1).join('/')];
    try {
      const { profile } = await updateCompanionProfile(worldId, { provider, model });
      if (profile) setCompanionProfile(profile);
    } catch (e) {
      console.error('Failed to update companion model:', e);
    }
  }, [worldId, setCompanionProfile]);

  if (authLoading || isLoadingWorld) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark-900">
        <div className="text-center">
          <div className="spinner w-8 h-8 mx-auto mb-4" />
          <p className="text-dark-200">Loading world...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !worldState) {
    return null;
  }

  const isCompanion = worldState.world.simulation_type === 'companion';

  return (
    <div className="flex h-screen bg-dark-900">
      <Sidebar
        worlds={worlds}
        currentWorldId={worldId}
        onCreateWorld={() => setShowCreateModal(true)}
        onImportWorld={() => setShowImportWizard(true)}
        onOpenSettings={() => setShowSettings(true)}
      />
      
      <main className="flex-1 min-w-0 min-h-0 overflow-hidden flex flex-col">
        {/* Header bar when sidebar is closed (hidden on mobile — NarrativeView has its own hamburger) */}
        {!sidebarOpen && !isMobile && (
          <div className="sticky top-0 z-30 bg-dark-800 border-b border-dark-500 px-4 py-3 flex items-center gap-3">
            <button
              onClick={toggleSidebar}
              className="text-dark-200 hover:text-dark-50 p-1"
              aria-label="Open menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <Link to="/" className="flex items-center gap-2">
              <img src="/vite.svg" alt="Textborne logo" className="h-5 w-5" />
              <span className="text-lg font-bold text-primary-400">Textborne</span>
            </Link>
          </div>
        )}
        {/* Admin Bar */}
        {isAdmin && (
          <div className="bg-primary-900/30 border-b border-primary-700 px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-primary-400 font-medium">🔧 Admin Mode</span>
              {!isMobile && (
                <>
                  <span className="text-primary-300/70">|</span>
                  <span className="text-primary-300/70">Hover messages for actions</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              {!isMobile && isCompanion && availableModels.length > 0 && (
                <select
                  value={companionProfile?.provider && companionProfile?.model
                    ? `${companionProfile.provider}/${companionProfile.model}`
                    : 'default'}
                  onChange={(e) => handleCompanionModelChange(e.target.value)}
                  className="text-xs bg-dark-700 border border-dark-500 text-dark-100 rounded px-2 py-1 max-w-[220px]"
                >
                  <option value="default">Model: Default (task config)</option>
                  {Object.entries(
                    availableModels.reduce<Record<string, ModelInfo[]>>((acc, m) => {
                      (acc[m.provider] ??= []).push(m);
                      return acc;
                    }, {})
                  ).map(([provider, models]) => (
                    <optgroup key={provider} label={provider}>
                      {models.map((m) => (
                        <option key={m.id} value={`${m.provider}/${m.modelId}`}>
                          {m.displayName}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              )}
              <Link
                to={`/admin/worlds/${worldId}`}
                state={{ returnTo: location.pathname }}
                className="text-xs bg-primary-600 hover:bg-primary-500 text-white px-3 py-1 rounded"
              >
                Inspect World →
              </Link>
            </div>
          </div>
        )}
        
        <div className="flex-1 min-w-0 min-h-0 overflow-hidden">
          <NarrativeView
            messages={messages}
            isStreaming={isStreaming}
            streamingContent={streamingContent}
            onSendMessage={sendUserInput}
            onEditMessage={editMessage}
            onDeleteMessage={deleteMessage}
            onRegenerateMessage={regenerateResponse}
            onBranchMessage={isCompanion ? undefined : handleBranchMessage}
            onStopGeneration={stopGeneration}
            onLoadOlder={loadOlderMessages}
            worldTitle={worldState.world.title}
            worldId={worldId || ''}
            worldSeed={worldState.seed}
            onSettingsUpdated={handleSettingsUpdated}
            isCompanion={isCompanion}
            onOpenCompanionMemories={() => setShowCompanionMemories(true)}
            onOpenCompanionSettings={() => setShowCompanionSettings(true)}
          />
        </div>
      </main>

      {!isCompanion && <WorldRail activeCharacters={worldState.activeCharacters} />}

      <CreateWorldWizard
        isOpen={showCreateModal}
        onClose={() => { setShowCreateModal(false); setImportHandoff(null); }}
        initialData={importHandoff}
      />

      <ImportWorldWizard
        isOpen={showImportWizard}
        onClose={() => setShowImportWizard(false)}
        onHandoffToCreate={(data) => {
          setImportHandoff(data);
          setShowImportWizard(false);
          setShowCreateModal(true);
        }}
      />

      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
      />

      {isCompanion && worldId && (
        <>
          <CompanionMemoryModal
            isOpen={showCompanionMemories}
            onClose={() => setShowCompanionMemories(false)}
            worldId={worldId}
          />
          <CompanionSettingsModal
            isOpen={showCompanionSettings}
            onClose={() => setShowCompanionSettings(false)}
            worldId={worldId}
          />
        </>
      )}

      <CreditRequiredModal
        isOpen={showCreditRequired}
        onClose={() => {
          setDismissedCreditNoticeId(insufficientCreditsNotice?.id ?? null);
          setShowCreditRequired(false);
        }}
        context="insufficient_credits"
      />
    </div>
  );
}
